import { type IntranetColleagueProfile } from '@gb-ticket/shared-types';
import {
  CakeIcon,
  Loader2Icon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  UserRoundIcon,
  type LucideIcon,
} from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AbsenceNoteStack } from '@/products/intranet/components/absence-note-banner';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { ColleagueChatButton } from '@/products/intranet/components/colleague-chat-button';
import { ZoomableProfilePhoto } from '@/platform/features/profile/profile-photo-preview';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import { formatColleagueBirthday } from '@/products/intranet/features/intranet/colleague-profile';
import { useIntranetColleagueProfile } from '@/products/intranet/features/intranet/use-intranet-directory';

type ColleagueProfileContextValue = {
  open: (userId: string) => void;
  close: () => void;
};

const ColleagueProfileContext = createContext<ColleagueProfileContextValue | null>(null);

/** One right-hand sheet for the whole intranet. Call `open(userId)` from any page. */
export function ColleagueProfileProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const open = useCallback((id: string) => {
    const active = document.activeElement;
    triggerRef.current = active instanceof HTMLElement ? active : null;
    setUserId(id);
  }, []);
  const close = useCallback(() => setUserId(null), []);

  return (
    <ColleagueProfileContext.Provider value={{ open, close }}>
      {children}
      <ColleagueProfileSheet
        userId={userId}
        open={userId !== null}
        onOpenChange={(next) => {
          if (!next) {
            setUserId(null);
          }
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      />
    </ColleagueProfileContext.Provider>
  );
}

/** Opens the shared colleague sheet. Only works under `ColleagueProfileProvider`. */
// eslint-disable-next-line react-refresh/only-export-components -- hook paired with the provider
export function useColleagueProfile(): ColleagueProfileContextValue {
  const value = useContext(ColleagueProfileContext);
  if (!value) {
    throw new Error('useColleagueProfile needs ColleagueProfileProvider');
  }
  return value;
}

function ColleagueProfileSheet({
  userId,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus: (event: Event) => void;
}) {
  const profile = useIntranetColleagueProfile(userId);
  const data = profile.data;
  const notFound =
    profile.isError &&
    profile.error instanceof ApiRequestError &&
    profile.error.error.statusCode === 404;
  const title = data?.displayName ?? 'Profil';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="gap-0 overflow-hidden p-0 sm:max-w-md"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {data ? (
          <ColleagueProfileBody profile={data} />
        ) : (
          <>
            <SheetHeader className="border-b pr-12">
              <SheetTitle>{profile.isPending ? 'Profil' : title}</SheetTitle>
            </SheetHeader>
            <div className="px-4 py-4">
              {profile.isPending ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Profil wird geladen …
                </p>
              ) : notFound ? (
                <p className="text-sm text-muted-foreground">Dieses Profil ist nicht verfügbar.</p>
              ) : profile.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  Das Profil konnte nicht geladen werden.
                </p>
              ) : null}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ColleagueProfileBody({ profile }: { profile: IntranetColleagueProfile }) {
  const me = useMe();
  const roleLine = [profile.jobTitle, profile.departmentName]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
    .join(' · ');
  const siteName = profile.siteName?.trim() ?? '';
  const birthday =
    profile.birthdayMonth != null && profile.birthdayDay != null
      ? formatColleagueBirthday(profile.birthdayMonth, profile.birthdayDay)
      : null;
  const isSelf = me.data?.id === profile.id;
  const aboutMe = profile.aboutMe?.trim() ?? '';

  const facts: { label: string; value: string; icon: LucideIcon; span?: boolean }[] = [];
  if (siteName) {
    facts.push({ label: 'Standort', value: siteName, icon: MapPinIcon });
  }
  if (profile.managerName) {
    facts.push({
      label: 'Vorgesetzte Person',
      value: profile.managerName,
      icon: UserRoundIcon,
      span: true,
    });
  }
  if (birthday) {
    facts.push({ label: 'Geburtstag', value: birthday, icon: CakeIcon });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SheetHeader className="border-b bg-brand-navy-soft/50 pr-12">
        <div className="flex items-center gap-3">
          <ZoomableProfilePhoto
            name={profile.displayName}
            profilePhotoUrl={profile.profilePhotoUrl}
            className="shrink-0 overflow-hidden ring-2 ring-background"
          >
            <ColleaguePortrait
              displayName={profile.displayName}
              profilePhotoUrl={profile.profilePhotoUrl}
              className="size-16 text-base"
            />
          </ZoomableProfilePhoto>
          <div className="min-w-0">
            <SheetTitle className="truncate text-lg">{profile.displayName}</SheetTitle>
            {roleLine ? <p className="text-sm text-muted-foreground">{roleLine}</p> : null}
          </div>
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {profile.absenceNote ? (
          <AbsenceNoteStack
            notes={[
              {
                id: profile.id,
                title: profile.absenceNote.label,
                body: profile.absenceNote.body,
              },
            ]}
          />
        ) : null}
        <section className="overflow-hidden rounded-lg border">
          <ContactRow
            label="E-Mail"
            href={`mailto:${profile.email}`}
            value={profile.email}
            icon={MailIcon}
          />
          {profile.phone ? (
            <ContactRow
              label="Telefon"
              href={`tel:${profile.phone}`}
              value={profile.phone}
              icon={PhoneIcon}
            />
          ) : null}
        </section>

        {facts.length > 0 ? (
          <dl className="grid grid-cols-2 gap-2">
            {facts.map((fact) => {
              const Icon = fact.icon;
              return (
                <div
                  key={fact.label}
                  className={`rounded-lg border bg-muted/30 px-3 py-2${fact.span ? 'col-span-2' : ''}`}
                >
                  <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Icon className="size-3.5 shrink-0" aria-hidden />
                    {fact.label}
                  </dt>
                  <dd className="mt-1 text-sm font-medium break-words">{fact.value}</dd>
                </div>
              );
            })}
          </dl>
        ) : null}

        {aboutMe ? (
          <section className="rounded-lg border bg-muted/20 px-3 py-3">
            <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Über mich
            </h3>
            <p className="mt-2 text-sm break-words whitespace-pre-wrap">{aboutMe}</p>
          </section>
        ) : null}
      </div>

      {isSelf ? null : (
        <div className="border-t px-4 py-4">
          <ColleagueChatButton
            userId={profile.id}
            reachesViaTeams={profile.reachesViaTeams}
            className="w-full"
            size="default"
          />
        </div>
      )}
    </div>
  );
}

function ContactRow({
  label,
  href,
  value,
  icon: Icon,
}: {
  label: string;
  href: string;
  value: string;
  icon: typeof MailIcon;
}) {
  return (
    <div className="flex items-start gap-3 border-b px-3 py-2.5 last:border-b-0">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <a href={href} className="text-sm break-all text-primary hover:underline">
          {value}
        </a>
      </div>
    </div>
  );
}
